import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_PACK_ANALYSIS_UOM_LADDER_RESOLVE,
  API_PACK_ANALYSIS_UOM_LADDER_LIST,
  API_PACK_ANALYSIS_UOM_LADDER_CREATE,
  API_PACK_ANALYSIS_UOM_LADDER_SUPERSEDE,
  API_PACK_ANALYSIS_UOM_LADDER_DELETE,
} from '../../constants/apiConstants';

// ============================================================================
// Types — mirror SmartMatrix.Domain.PackAnalysis.Messages.UomLadder_*.
// ============================================================================

/** Effective ladder for one item under a tenant/buyer/supplier/channel. */
export interface UomLadderResolved {
  itemId: number;
  levels: UomLadderLevel[];
}

/** One level in the resolved ladder, per UomLadderLevel_OutputPayload. */
export interface UomLadderLevel {
  level: number;                 // 0=EACH, 1=INNER, 2=CASE, 3=LAYER, 4=PALLET
  uomCode: string;
  factorToLower: number;
  effectiveUnitsToEach: number;  // pre-computed cumulative product
  gtin?: string | null;
  resolvedScopePath: string;
  resolvedScopeLevel: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/** One row in the admin list — single-row projection (NOT the resolved ladder). */
export interface UomLadderRow {
  id: number;
  scopePath: string;
  scopeLevel: number;
  level: number;
  uomCode: string;
  factorToLower: number;
  gtin?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  source?: string | null;
  internalRemark?: string | null;
  isActive: boolean;
  createdAt?: string | null;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
}

export interface UomLadderResolveParams {
  tenantId: number;
  itemId: number;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  channelCode?: string | null;
  asOf?: string | null;          // ISO; defaults server-side to UtcNow
}

export interface UomLadderListFilters {
  tenantId: number;              // FE always supplies tenantId so axios includes it as a query param
  scopePathPrefix?: string;
  scopeLevel?: number;
  includeExpired?: boolean;
  pageIndex?: number;
  pageSize?: number;
}

export interface UomLadderListResponse {
  items: UomLadderRow[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

export interface UomLadderCreateRequest {
  scopePath: string;             // server validates + computes scope_level
  level: number;
  uomCode: string;
  factorToLower: number;
  gtin?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  source?: string | null;
  internalRemark?: string | null;
}

export interface UomLadderSupersedeRequest {
  newEffectiveFrom: string;
  uomCode?: string | null;
  factorToLower?: number | null;
  gtin?: string | null;
  effectiveTo?: string | null;
  source?: string | null;
  internalRemark?: string | null;
}

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

// ============================================================================
// Service
// ============================================================================

export const uomLadderService = {
  async resolve(params: UomLadderResolveParams): Promise<UomLadderResolved> {
    try {
      const response = await apiClient.get<ResultEnvelope<UomLadderResolved>>(
        API_PACK_ANALYSIS_UOM_LADDER_RESOLVE,
        {
          params: {
            tenantId: params.tenantId,
            itemId: params.itemId,
            buyerPartyId: params.buyerPartyId ?? undefined,
            supplierPartyId: params.supplierPartyId ?? undefined,
            channelCode: params.channelCode ?? undefined,
            asOf: params.asOf ?? undefined,
          },
        }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to resolve UoM ladder');
      return response.data.data ?? { itemId: params.itemId, levels: [] };
    } catch (error) {
      logger.error('Failed to resolve UoM ladder', error);
      throw error;
    }
  },

  async list(filters: UomLadderListFilters): Promise<UomLadderListResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<UomLadderListResponse>>(
        API_PACK_ANALYSIS_UOM_LADDER_LIST,
        {
          params: {
            tenantId: filters.tenantId,
            scopePathPrefix: filters.scopePathPrefix || undefined,
            scopeLevel: filters.scopeLevel,
            includeExpired: filters.includeExpired,
            pageIndex: filters.pageIndex ?? 1,
            pageSize: filters.pageSize ?? 50,
          },
        }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to list UoM ladders');
      return response.data.data ?? { items: [], pageIndex: 1, pageSize: 50, totalCount: 0 };
    } catch (error) {
      logger.error('Failed to list UoM ladders', error);
      throw error;
    }
  },

  async create(tenantId: number, request: UomLadderCreateRequest): Promise<{ id: number }> {
    try {
      const response = await apiClient.post<ResultEnvelope<{ id: number }>>(
        API_PACK_ANALYSIS_UOM_LADDER_CREATE,
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to create UoM ladder');
      toast.success('UoM ladder row created');
      return response.data.data ?? { id: 0 };
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create UoM ladder: ${msg}`);
      throw error;
    }
  },

  async supersede(tenantId: number, id: number, request: UomLadderSupersedeRequest): Promise<{ id: number }> {
    try {
      const response = await apiClient.put<ResultEnvelope<{ id: number }>>(
        API_PACK_ANALYSIS_UOM_LADDER_SUPERSEDE(id),
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to supersede UoM ladder');
      toast.success('UoM ladder row superseded');
      return response.data.data ?? { id: 0 };
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to supersede UoM ladder: ${msg}`);
      throw error;
    }
  },

  async remove(tenantId: number, id: number): Promise<void> {
    try {
      const response = await apiClient.delete<ResultEnvelope<unknown>>(
        API_PACK_ANALYSIS_UOM_LADDER_DELETE(id),
        { params: { tenantId } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to delete UoM ladder');
      toast.success('UoM ladder row deleted');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete UoM ladder: ${msg}`);
      throw error;
    }
  },
};
